/**
 * Model Portfolio Tab — Session 147j+17p (Feb 2026).
 *
 * UI for the model_portfolio_positions collection. Read-only, both
 * admin and user surfaces. Powered by:
 *   GET /api/model-portfolio/positions?status=open|history|all
 *   GET /api/model-portfolio/summary
 *
 * Design goals
 * ------------
 * - KPI strip at top for at-a-glance performance
 * - Sortable + filterable table with every metric the backend tracks
 * - Simple per-position return bar chart (equity-curve is Phase 3;
 *   we currently only have snapshot data, not daily time-series)
 * - Deep-link back to the source LODR order (opens Order Wins tab
 *   pre-filtered on that symbol via `?lodrSymbol=…`)
 *
 * All data comes from the same backend snapshot the daily 17:15 IST
 * cron writes; there are no client-side calculations of returns.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, Cell,
} from 'recharts';
import {
  ArrowUp, ArrowDown, Package, ExternalLink, TrendingUp, TrendingDown, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmt = (v, digits = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(digits));
const fmtPct = (v, digits = 2) => (v == null || Number.isNaN(v) ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(digits)}%`);
const fmtCr = (v) => (v == null ? '—' : `₹${Number(v).toFixed(1)} Cr`);
const daysBetween = (dateStr) => {
  if (!dateStr) return null;
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000)));
};
const toneForPct = (v) => {
  if (v == null) return 'text-zinc-400';
  if (v > 5) return 'text-emerald-400 font-semibold';
  if (v > 0) return 'text-emerald-300';
  if (v > -5) return 'text-rose-300';
  return 'text-rose-400 font-semibold';
};

// ─── KPI strip ────────────────────────────────────────────────
const KPI = ({ label, value, tone, sub, testId }) => (
  <div
    className="rounded border border-zinc-800 bg-zinc-950/60 p-3 min-w-[140px]"
    data-testid={testId}
  >
    <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-xl font-bold ${tone || 'text-zinc-100'}`}>{value}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const KpiStrip = ({ summary }) => {
  if (!summary) return null;
  const openStats = summary.open || {};
  const historyStats = summary.history || {};
  return (
    <div className="flex flex-wrap gap-3" data-testid="mp-kpi-strip">
      <KPI
        testId="mp-kpi-open-count"
        label="Open positions"
        value={openStats.count ?? 0}
        sub={`+ ${historyStats.count ?? 0} history`}
      />
      <KPI
        testId="mp-kpi-hit-rate"
        label="Hit rate (open)"
        value={openStats.hit_rate_pct != null ? `${openStats.hit_rate_pct}%` : '—'}
        tone={openStats.hit_rate_pct > 50 ? 'text-emerald-400' : openStats.hit_rate_pct >= 40 ? 'text-amber-300' : 'text-rose-400'}
      />
      <KPI
        testId="mp-kpi-avg-return"
        label="Avg return"
        value={fmtPct(openStats.avg_return_pct)}
        tone={toneForPct(openStats.avg_return_pct)}
      />
      <KPI
        testId="mp-kpi-median-return"
        label="Median return"
        value={fmtPct(openStats.median_return_pct)}
        tone={toneForPct(openStats.median_return_pct)}
      />
      <KPI
        testId="mp-kpi-best"
        label="Best position"
        value={fmtPct(openStats.best_return_pct)}
        tone="text-emerald-400"
      />
      <KPI
        testId="mp-kpi-worst"
        label="Worst position"
        value={fmtPct(openStats.worst_return_pct)}
        tone="text-rose-400"
      />
    </div>
  );
};

// ─── Per-position return bar chart ────────────────────────────
const ReturnBarChart = ({ positions }) => {
  const data = useMemo(
    () => positions
      .filter((p) => p.return_pct != null)
      .map((p) => ({
        symbol: p.symbol,
        return_pct: p.return_pct,
      }))
      .sort((a, b) => b.return_pct - a.return_pct)
      .slice(0, 40),
    [positions],
  );
  if (!data.length) return null;
  return (
    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3" data-testid="mp-return-chart">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
        Returns by position (top 40 by absolute return)
      </div>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 6, right: 6, left: -12, bottom: 20 }}>
            <XAxis dataKey="symbol" tick={{ fill: '#71717a', fontSize: 9 }} angle={-45} textAnchor="end" interval={0} />
            <YAxis tick={{ fill: '#71717a', fontSize: 10 }} unit="%" />
            <RTooltip
              contentStyle={{ background: '#0a0a0a', border: '1px solid #3f3f46', fontSize: 11 }}
              formatter={(v) => [`${v.toFixed(2)}%`, 'Return']}
              labelStyle={{ color: '#e4e4e7' }}
            />
            <Bar dataKey="return_pct">
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.return_pct >= 0 ? '#10b981' : '#f43f5e'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─── Positions table ──────────────────────────────────────────
const HEADERS = [
  { id: 'symbol', label: 'Symbol', numeric: false },
  { id: 'entry_date', label: 'Entry', numeric: false },
  { id: 'days_held', label: 'Days', numeric: true, compute: (p) => daysBetween(p.entry_date) },
  { id: 'entry_price', label: 'Entry ₹', numeric: true },
  { id: 'latest_price', label: 'Now ₹', numeric: true },
  { id: 'return_pct', label: 'Return %', numeric: true },
  { id: 'return_vs_nifty50_pct', label: 'vs N50', numeric: true },
  { id: 'return_vs_nifty500_pct', label: 'vs N500', numeric: true },
  { id: 'high_since_entry', label: 'High ₹', numeric: true },
  { id: 'low_since_entry', label: 'Low ₹', numeric: true },
  { id: 'drawdown_from_peak_pct', label: 'DD', numeric: true },
  { id: 'orders_count_since_entry', label: 'Orders', numeric: true },
  { id: 'orders_total_cr_since_entry', label: 'Order ₹Cr', numeric: true },
  { id: 'llm_confidence_at_entry', label: 'Conf', numeric: true },
  { id: 'lodr_link', label: 'Source', numeric: false },
];

const openLodrForSymbol = (symbol) => {
  // Deep-link into the Order Wins tab, pre-filtered on this symbol.
  // The AdminDashboard reads `activeTab` from the sidebar click and
  // LODROrdersTab honors `?lodrSymbol=…` on its symbol filter.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('activeTab', 'lodr-orders');
    url.searchParams.set('lodrSymbol', symbol);
    window.history.pushState({}, '', url);
    // Force React state update via a custom event + fallback reload
    window.dispatchEvent(new CustomEvent('mp-nav-lodr', { detail: { symbol } }));
    // Fallback: force reload if event listener not wired yet
    setTimeout(() => {
      if (!document.querySelector('[data-testid="lodr-orders-table"]')) {
        window.location.reload();
      }
    }, 300);
  } catch (e) {
    toast.error('Could not open source LODR order');
  }
};

const PositionsTable = ({ positions, sortKey, sortDir, onSort, readOnly = false }) => (
  <div className="rounded border border-zinc-800 bg-zinc-950/40 overflow-x-auto" data-testid="mp-positions-table">
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-zinc-800 text-zinc-500 uppercase text-[9px] tracking-wider">
          {HEADERS.map((h) => (
            <th
              key={h.id}
              className={`px-2 py-1.5 ${h.numeric ? 'text-right' : 'text-left'} cursor-pointer hover:text-zinc-200 select-none whitespace-nowrap`}
              onClick={() => onSort(h.id)}
              data-testid={`mp-th-${h.id}`}
            >
              {h.label}
              {sortKey === h.id && (
                <span className="ml-0.5 inline-flex align-middle">
                  {sortDir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody data-testid="mp-positions-tbody">
        {positions.length === 0 && (
          <tr>
            <td colSpan={HEADERS.length} className="py-8 text-center text-zinc-500 italic">
              {readOnly
                ? 'No positions yet — the portfolio populates as the LLM issues BUY verdicts on new orders.'
                : (<>
                    No positions yet. Run <code className="text-zinc-300">POST /api/admin/model-portfolio/backfill?days=90</code>
                    &nbsp;from the admin panel to seed.
                  </>)}
            </td>
          </tr>
        )}
        {positions.map((p) => {
          const held = daysBetween(p.entry_date);
          return (
            <tr
              key={p._id}
              className="border-b border-zinc-900 hover:bg-zinc-900/40 transition"
              data-testid={`mp-row-${p.symbol}`}
            >
              <td className="px-2 py-1.5 font-mono font-semibold text-zinc-100" data-testid={`mp-cell-symbol-${p.symbol}`}>
                {p.symbol}
                {p.order_tier_at_entry && (
                  <span
                    className={`ml-1 text-[8px] px-1 py-0.5 rounded ${p.order_tier_at_entry === 'STRONG' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'}`}
                  >
                    {p.order_tier_at_entry}
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 text-zinc-400 font-mono text-[10px]">{p.entry_date}</td>
              <td className="px-2 py-1.5 text-right text-zinc-400 font-mono">{held ?? '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{fmt(p.entry_price)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-zinc-100">{fmt(p.latest_price)}</td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(p.return_pct)}`} data-testid={`mp-cell-return-${p.symbol}`}>
                {p.return_pct >= 0 ? <TrendingUp size={10} className="inline mr-0.5" /> : <TrendingDown size={10} className="inline mr-0.5" />}
                {fmtPct(p.return_pct)}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(p.return_vs_nifty50_pct)}`}>
                {fmtPct(p.return_vs_nifty50_pct)}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(p.return_vs_nifty500_pct)}`}>
                {fmtPct(p.return_vs_nifty500_pct)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-emerald-300/80" title={p.high_since_entry_date || ''}>
                {fmt(p.high_since_entry)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-rose-300/80" title={p.low_since_entry_date || ''}>
                {fmt(p.low_since_entry)}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(p.drawdown_from_peak_pct)}`}>
                {fmtPct(p.drawdown_from_peak_pct)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-zinc-300">
                <Package size={9} className="inline mr-0.5 opacity-60" />
                {p.orders_count_since_entry ?? 0}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-zinc-300">
                {fmtCr(p.orders_total_cr_since_entry)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-zinc-400">
                {p.llm_confidence_at_entry != null ? `${(p.llm_confidence_at_entry * 100).toFixed(0)}%` : '—'}
              </td>
              <td className="px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => openLodrForSymbol(p.symbol)}
                  className="text-[10px] text-blue-300 hover:text-blue-200 hover:underline flex items-center gap-0.5"
                  data-testid={`mp-source-btn-${p.symbol}`}
                >
                  Open <ExternalLink size={9} />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ─── Main tab ─────────────────────────────────────────────────
const ModelPortfolioTab = ({ readOnly = false } = {}) => {
  const [status, setStatus] = useState('open');
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sortKey, setSortKey] = useState('return_pct');
  const [sortDir, setSortDir] = useState('desc');
  const [tierFilter, setTierFilter] = useState('ALL');
  const [minDaysHeld, setMinDaysHeld] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, sumRes] = await Promise.all([
        axios.get(`${API}/api/model-portfolio/positions?status=${status}&limit=500`, { headers: authHeaders() }),
        axios.get(`${API}/api/model-portfolio/summary`, { headers: authHeaders() }),
      ]);
      setPositions(posRes.data?.positions || []);
      setSummary(sumRes.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load model portfolio');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/model-portfolio/backfill?days=90`, null, { headers: authHeaders() });
      toast.success(`Backfill: opened ${r.data.opened}, skipped ${r.data.skipped}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const runRefresh = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/model-portfolio/refresh`, null, { headers: authHeaders() });
      toast.success(`Refreshed ${r.data.refreshed} · auto-closed ${r.data.auto_closed}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let list = [...positions];
    if (tierFilter !== 'ALL') list = list.filter((p) => p.order_tier_at_entry === tierFilter);
    if (minDaysHeld > 0) list = list.filter((p) => (daysBetween(p.entry_date) ?? 0) >= minDaysHeld);
    const hdr = HEADERS.find((h) => h.id === sortKey);
    const getter = hdr?.compute || ((p) => p[sortKey]);
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return list;
  }, [positions, sortKey, sortDir, tierFilter, minDaysHeld]);

  return (
    <div className="space-y-4" data-testid="model-portfolio-tab">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-zinc-100">Model Portfolio</h1>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Every LLM BUY tracked for exactly 365 days · equal-weight ₹1L notional · benchmark:
            Nifty 50 &amp; Nifty 500 (plain price)
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {!readOnly && (
            <>
              <button
                type="button"
                onClick={runRefresh}
                disabled={busy}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-emerald-400/60 hover:text-emerald-200 transition disabled:opacity-40 font-mono flex items-center gap-1"
                data-testid="mp-refresh-btn"
              >
                <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Refresh prices
              </button>
              <button
                type="button"
                onClick={runBackfill}
                disabled={busy}
                className="px-3 py-1.5 text-xs rounded border border-blue-500/60 bg-blue-500/15 hover:bg-blue-500/25 text-blue-100 transition disabled:opacity-40 font-mono"
                data-testid="mp-backfill-btn"
              >
                Backfill 90d
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <KpiStrip summary={summary} />

      {/* Chart */}
      <ReturnBarChart positions={filtered} />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Filters:</div>
        <div className="inline-flex rounded border border-zinc-800 overflow-hidden" data-testid="mp-status-toggle">
          {['open', 'history', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`px-2.5 py-1 font-mono uppercase text-[10px] ${status === s ? 'bg-blue-500/25 text-blue-100' : 'text-zinc-400 hover:text-zinc-200'}`}
              data-testid={`mp-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded border border-zinc-800 overflow-hidden" data-testid="mp-tier-toggle">
          {['ALL', 'STRONG', 'MODERATE'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTierFilter(t)}
              className={`px-2.5 py-1 font-mono uppercase text-[10px] ${tierFilter === t ? 'bg-emerald-500/25 text-emerald-100' : 'text-zinc-400 hover:text-zinc-200'}`}
              data-testid={`mp-tier-${t.toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>
        <label className="text-[10px] text-zinc-500 flex items-center gap-1 font-mono">
          Min days held
          <input
            type="number"
            min={0}
            max={365}
            value={minDaysHeld}
            onChange={(e) => setMinDaysHeld(Number(e.target.value) || 0)}
            className="w-14 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-200 font-mono"
            data-testid="mp-min-days-input"
          />
        </label>
        <div className="text-[10px] text-zinc-500 ml-auto font-mono">
          {loading ? 'loading…' : `${filtered.length} position${filtered.length === 1 ? '' : 's'} shown`}
        </div>
      </div>

      {/* Table */}
      <PositionsTable
        positions={filtered}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        readOnly={readOnly}
      />
    </div>
  );
};

export default ModelPortfolioTab;
